import React, { useContext, useEffect } from 'react';
import { Button, Card, message, Icon, Tooltip, Color } from '@bb/elements-web';
import styled from 'styled-components';
import { AuthContext } from 'contexts';
import { AnsweringServiceType } from '@bb/domain/dist/types/AnsweringSubscription';
import AnsweringServiceContext, {
  AnsweringServiceSubscribeStep,
} from '../../AnsweringServiceContext';
import { TitleCircle } from '../../StyledComponents';

type LiveConciergeCardProps = {
  showEstimator?: boolean;
  onToggleEstimator?: () => void;
};

/**
 * The Live Concierge card that appears inside the Learn More step of the
 * Answering Service subscription signup flow.
 */
const LiveConciergeCard: React.FC<LiveConciergeCardProps> = ({
  showEstimator = false,
  onToggleEstimator,
}) => {
  const { currentSubscriptionType, setCurrentStep } = useContext(
    AnsweringServiceContext,
  );
  const { viewingLevel } = useContext(AnsweringServiceContext);
  const { hasLimitedAccess } = useContext(AuthContext);

  /** The text to show for the Subscribe button tooltip. */
  const [tooltipText, setTooltipText] = React.useState(''); // A blank string will hide the tooltip.
  useEffect(() => {
    if (hasLimitedAccess)
      setTooltipText(
        'You do not have access to this feature. Please reach out to your managing broker to finish setting up your brokerage.',
      );
    if (!currentSubscriptionType)
      setTooltipText(
        'There was an error fetching the Answering Service subscription data for your organization. Please contact your brokerage.',
      );
  }, [hasLimitedAccess, currentSubscriptionType]);

  const handleToggleEstimatorClick = () => {
    if (onToggleEstimator) {
      onToggleEstimator();
    } else {
      message.info('Coming Soon');
    }
  };

  const defaultBulletItems = [
    'US and Canada-based live answering service',
    'Staffed by showing management specialists',
    'Handles incoming and outgoing calls for your listings',
    'Includes showing requests, confirmations, and client follow-ups',
  ];

  const hasAppointmentDeskBulletItems = [
    'Includes all Appointment Desk features, plus',
    'Outgoing calls for your listings, including appointment coordination and client follow-ups',
  ];

  const bulletItems =
    currentSubscriptionType === AnsweringServiceType.AppointmentDesk
      ? hasAppointmentDeskBulletItems
      : defaultBulletItems;

  const cardTitle = (
    <TitleContainer>
      <TitleCircle>
        <Icon
          icon="liveConcierge"
          size={26}
          color="primary"
          style={{ display: 'flex' }}
        />
      </TitleCircle>
      <span>Live Concierge</span>
    </TitleContainer>
  );

  return (
    <StyledCard title={cardTitle} $withContent={false} dataCy="lcs-card">
      <div>
        <HeaderRow>
          <HeaderContent>
            <SubLabel>Custom</SubLabel>
            <Description>
              Save more as you scale! Higher volumes unlock better rates.
            </Description>
          </HeaderContent>
        </HeaderRow>
        {/* Only show the price estimator toggle for org viewing level */}
        {viewingLevel === 'org' && (
          <ClickableText onClick={handleToggleEstimatorClick}>
            {showEstimator ? 'Hide Price Estimator' : 'Show Price Estimator'}
          </ClickableText>
        )}
        <Tooltip title={tooltipText}>
          <span>
            <SubscribeButton
              type="primary"
              dataCy="subscribe-btn"
              disabled={hasLimitedAccess || !currentSubscriptionType}
              onClick={() => {
                // If they have an active Appt Desk subscription, they already viewed the Manage Listings page earlier.
                // So skip to Payment Confirmation.
                currentSubscriptionType === AnsweringServiceType.AppointmentDesk
                  ? setCurrentStep(
                      AnsweringServiceSubscribeStep.PaymentConfirmation,
                    )
                  : setCurrentStep(
                      AnsweringServiceSubscribeStep.ManageListings,
                    );
              }}
            >
              Subscribe
            </SubscribeButton>
          </span>
        </Tooltip>
        <Divider />
        <BulletList>
          {bulletItems.map((item, index) => (
            <BulletItem key={index}>
              <Icon
                icon="checkOutlined"
                style={{ color: '#2c6ef2' }}
                size={16}
              />
              <span>{item}</span>
            </BulletItem>
          ))}
        </BulletList>
      </div>
    </StyledCard>
  );
};

export default LiveConciergeCard;

export const StyledCard = styled(Card)<{ $withContent?: boolean }>`
  width: 501px;

  .ant-card-head {
    border-bottom: 0px;
  }

  .ant-card-body {
    padding: 0px 24px 24px 24px;
  }

  ${({ $withContent }) =>
    $withContent &&
    `.ant-card-body {
      padding: 1px 0px;
    }`}
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 20px;
  font-weight: 600;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const SubLabel = styled.div`
  font-weight: 700;
  font-size: 24px;
  margin-bottom: 8px;
  color: #2b2b2b;
`;

const Description = styled.p`
  color: #585e72;
  font-size: 16px;
  font-weight: 400;
  margin: 0;
`;

const ClickableText = styled.button`
  color: ${Color.PRIMARY};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 20px;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    text-decoration: underline;
  }
`;

const SubscribeButton = styled(Button)`
  width: 100%;
  height: 40px;
  margin-bottom: 16px;
  font-size: 17px;
`;

const Divider = styled.div`
  height: 1px;
  background-color: #e8e8e8;
  margin-bottom: 16px;
`;

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const BulletItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  font-size: 16px;
  color: #1a1b21;
`;
